import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import FeedbackWorkspace from "../home_screns/web_application/FeedbackWorkspace";
import { EnhancedTokenManager } from "../../../utils/tokenManager";
import { API_URLS, BASE_URL } from "../../../api/urls";
import { extractCardIdFromUrl, buildCardUrl } from "../../../utils/urlIdEncoder";
import AvatarIcon from "../../../assets/images/profile-avatar.svg";
import { FeedbackItem, WorkspaceState } from "../../../types/feedback.types";
import { ScreenshotSubmission } from "../../ui/screenshot-screenrecord/Screenshot/Screenshot_Preview";
import { RecordingSubmission } from "../../ui/screenshot-screenrecord/screen-recording/ScreenRecordingPreview";
import { pipStore } from "../../../utils/pipStore";

interface DashboardOutletContext {
  onHideSidebar: (hide: boolean) => void;
}

interface RawDraftFeedback {
  id: number;
  url: string;
  screenshot_url: string | null;
  recording_url: string | null;
  voice_recording_url: string | null;
  message: string | null;
  status: "draft";
  created_at: string;
}

interface RawDraftCard {
  card_id: number;
  card_name: string;
  preview_screenshot: string | null;
  feedbacks: RawDraftFeedback[];
}



const Shimmer = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <div
    className={className}
    style={{
      background: "linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)",
      backgroundSize: "600px 100%",
      animation: "shimmer 1.6s ease-in-out infinite",
      borderRadius: 6,
      ...style,
    }}
  />
);

const WorkspacePageSkeleton: React.FC = () => (
  <div className="h-screen w-full bg-white flex flex-col overflow-hidden">
    <style>{`
      @keyframes shimmer {
        0%   { background-position: -600px 0; }
        100% { background-position:  600px 0; }
      }
    `}</style>

    {/* Header */}
    <header className="flex items-center justify-between px-6 border-b border-gray-100 flex-shrink-0 h-[54px]">
      <div className="flex items-center gap-3 flex-1">
        {/* Home button */}
        <Shimmer style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
        {/* Feedback name */}
        <Shimmer style={{ width: 140, height: 18 }} />
        {/* "New tab" bar */}
        <div
          className="flex items-center gap-2 flex-shrink-0"
          style={{ width: 333, height: 40, background: "#eff0f5", borderRadius: 10, padding: "0 12px" }}
        >
          <Shimmer style={{ width: 16, height: 16, borderRadius: 3, flexShrink: 0 }} />
          <div style={{ width: 1, height: 18, background: "#d0d0d0", flexShrink: 0 }} />
          <Shimmer style={{ flex: 1, height: 12, borderRadius: 4 }} />
        </div>
      </div>
      {/* Right actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Shimmer style={{ width: 24, height: 24, borderRadius: 6 }} />
        <Shimmer style={{ width: 110, height: 36, borderRadius: 8 }} />
        <Shimmer style={{ width: 130, height: 36, borderRadius: 8 }} />
      </div>
    </header>

    {/* Body */}
    <div className="flex flex-1 overflow-hidden">

      {/* Left sidebar */}
      <aside
        className="ml-2 flex flex-col flex-shrink-0 relative"
        style={{
          width: 112,
          background: "#f7f7f7",
          border: "0.5px solid #d7d9e2",
          borderBottom: "none",
          borderRadius: "10px 10px 0 0",
        }}
      >
        {/* Thumbnail list */}
        <div className="flex flex-col gap-2 p-2 flex-1 overflow-hidden" style={{ paddingBottom: 100 }}>
          <Shimmer style={{ width: 88, height: 52, borderRadius: 6 }} />
          <Shimmer style={{ width: 88, height: 52, borderRadius: 6, opacity: 0.7 }} />
          <Shimmer style={{ width: 88, height: 52, borderRadius: 6, opacity: 0.45 }} />
        </div>

        {/* Tool strip */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{ background: "#f7f7f7", borderTop: "0.5px solid #d7d9e2" }}
        >
          <div className="bg-white flex flex-col items-center gap-2 py-2">
            <div className="flex gap-2">
              <Shimmer style={{ width: 34, height: 34, borderRadius: 8 }} />
              <Shimmer style={{ width: 34, height: 34, borderRadius: 8 }} />
            </div>
            <Shimmer style={{ width: 70, height: 10, borderRadius: 4, opacity: 0.6 }} />
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden" style={{ padding: "0 16px 0 8px" }}>
        <div
          className="flex-1 flex flex-col items-center justify-center gap-6"
          style={{
            background: "#f9faff",
            border: "0.5px solid #e5e5e5",
            borderRadius: "10px 10px 0 0",
          }}
        >
          {/* Logo placeholder */}
          <Shimmer style={{ width: 120, height: 120, borderRadius: 12 }} />

          {/* URL bar */}
          <div
            className="flex items-center gap-3"
            style={{
              width: "100%",
              maxWidth: 520,
              padding: "0 20px",
            }}
          >
            <div
              className="flex items-center gap-2 w-full"
              style={{
                background: "#fff",
                border: "1px solid #000",
                borderRadius: 10,
                padding: "12px 14px",
                height: 50,
              }}
            >
              <Shimmer style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0 }} />
              <div style={{ width: 1, height: 20, background: "#b7bbca", flexShrink: 0 }} />
              <Shimmer style={{ flex: 1, height: 13, borderRadius: 4 }} />
              <Shimmer style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0 }} />
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
);

const getValidUrl = (path: string | null | undefined): string | undefined => {
  if (!path) return undefined;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

interface NewCardState {
  cardName: string;
  cardId: number;
  workspaceId: number;
}

interface PipRestoreState {
  cardDbId: number;
  workspaceId: number;
  feedbackName: string;
  feedbackItems: FeedbackItem[];
  workspaceState: WorkspaceState | null;
}

const WorkspacePage: React.FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { onHideSidebar } = useOutletContext<DashboardOutletContext>();

  const locationState = location.state as { newCard?: NewCardState; pipRestore?: PipRestoreState } | null;
  const newCard = locationState?.newCard;
  const pipRestore = locationState?.pipRestore;

  const [loading, setLoading] = useState(true);
  const [cardName, setCardName] = useState("");
  const [workspaceId, setWorkspaceId] = useState(0);
  const [cardDbId, setCardDbId] = useState(0);
  const [initialWorkspaceState, setInitialWorkspaceState] = useState<WorkspaceState | null>(null);
  const [initialFeedbackItems, setInitialFeedbackItems] = useState<FeedbackItem[]>([]);

  useEffect(() => {
    onHideSidebar(true);
    loadCard();
    return () => {
      onHideSidebar(false);
    };
  }, [cardId]);

  const loadCard = async () => {
    const numericCardId = extractCardIdFromUrl(cardId || '');
    if (!numericCardId) {
      navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
      return;
    }

    // Restore directly from PiP data — skip the API round-trip
    if (pipRestore && pipRestore.cardDbId === numericCardId) {
      setCardName(pipRestore.feedbackName);
      setCardDbId(pipRestore.cardDbId);
      setWorkspaceId(pipRestore.workspaceId);
      setInitialFeedbackItems(pipRestore.feedbackItems);
      setInitialWorkspaceState(pipRestore.workspaceState);
      setLoading(false);
      return;
    }

    const token = EnhancedTokenManager.getAccessToken();
    if (!token) {
      navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
      return;
    }

    const wId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
    if (!wId) {
      navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
      return;
    }
    setWorkspaceId(wId);

    try {
      const response = await fetch(API_URLS.GET_DRAFT(wId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch drafts");

      const data = await response.json();
      const entries = Object.entries(data) as [string, RawDraftCard][];
      const entry = entries.find(([, cd]) => cd.card_id === numericCardId);

      if (!entry) {
        // Newly created card — not in GET_DRAFT yet. Use navigation state directly.
        if (newCard && newCard.cardId === numericCardId) {
          setCardName(newCard.cardName);
          setCardDbId(newCard.cardId);
          setWorkspaceId(newCard.workspaceId);
          setInitialFeedbackItems([]);
          setInitialWorkspaceState(null);
          setLoading(false);
          return;
        }
        navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
        return;
      }

      const [, cardData] = entry;
      setCardName(cardData.card_name);
      setCardDbId(cardData.card_id);

      const feedbacks = cardData.feedbacks
        .filter((f) => f.message || f.screenshot_url || f.recording_url || f.voice_recording_url)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      const feedbackItems: FeedbackItem[] = feedbacks.map((f, index) => {
        const screenshotUrl = getValidUrl(f.screenshot_url);
        const recordingUrl = getValidUrl(f.recording_url);
        const audioUrl = getValidUrl(f.voice_recording_url);
        const hasRecording = !!(recordingUrl);
        const itemType: "screenshot" | "recording" = hasRecording ? "recording" : "screenshot";

        const item: FeedbackItem = {
          id: f.id.toString(),
          type: itemType,
          timestamp: new Date(f.created_at).getTime(),
          order: index + 1,
          feedback: f.message || "",
          hasAudio: !!audioUrl,
          audioUrl,
          audioFilename: audioUrl ? `audio_${f.id}.wav` : undefined,
          user: { name: "User", avatar: AvatarIcon },
          isSelected: false,
          apiFeedbackId: f.id,
          uniqueFeedbackId: f.id.toString(),
        };

        if (itemType === "screenshot") {
          item.annotatedImage = screenshotUrl;
          item.originalImage = screenshotUrl;
        } else {
          item.dataUrl = recordingUrl;
          item.duration = 30;
          item.recordingBlob = new Blob([], { type: "video/mp4" });
        }

        return item;
      });

      const screenshots: ScreenshotSubmission[] = feedbackItems
        .filter((item) => item.type === "screenshot" && item.annotatedImage)
        .map((item) => ({
          id: item.id,
          annotatedImage: item.annotatedImage!,
          originalImage: item.originalImage || item.annotatedImage!,
          feedback: item.feedback || "",
          timestamp: item.timestamp,
          hasAudio: item.hasAudio || false,
          audioUrl: item.audioUrl,
          audioFilename: item.audioFilename,
          uniqueFeedbackId: item.uniqueFeedbackId,
          audioBlob: undefined,
        }));

      const recordings: RecordingSubmission[] = feedbackItems
        .filter((item) => item.type === "recording" && item.dataUrl)
        .map((item) => ({
          id: item.id,
          recordingBlob: item.recordingBlob || new Blob([], { type: "video/mp4" }),
          dataUrl: item.dataUrl!,
          feedback: item.feedback || "",
          timestamp: item.timestamp,
          duration: item.duration || 30,
          hasAudio: item.hasAudio || false,
          uniqueFeedbackId: item.uniqueFeedbackId,
        }));

      const wsState: WorkspaceState = {
        screenshots,
        recordings,
        feedbackItemsOrder: feedbackItems.map((item) => `${item.type}-${item.id}`),
        url: feedbacks[0]?.url,
        iframeHistory: feedbacks[0]?.url ? [feedbacks[0].url] : [],
        iframeHistoryIndex: 0,
        showIframe: !!feedbacks[0]?.url,
        feedbackId: feedbacks[0]?.id?.toString(),
        isUrlLocked: feedbackItems.length > 0,
      };

      setInitialFeedbackItems(feedbackItems);
      setInitialWorkspaceState(feedbackItems.length > 0 ? wsState : null);
      setLoading(false);
    } catch (error) {
      console.error("Error loading workspace card:", error);
      navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
    }
  };

  if (loading) return <WorkspacePageSkeleton />;

  return (
    <FeedbackWorkspace
      feedbackName={cardName}
      onBack={(deletionState) => {
        if (deletionState?.shareSuccess) {
          // Shared: land on "created" tab, refresh both created and draft lists
          window.dispatchEvent(new CustomEvent("refresh-draft-tab"));
          navigate("/dashboard", {
            state: { activeTab: "created", refreshTab: "all" },
            replace: true,
          });
        } else {
          if (deletionState?.cardDeleted) {
            window.dispatchEvent(new CustomEvent("refresh-draft-tab"));
          }
          navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
        }
      }}
      onSaveAndExit={(_items, _state) => {
        navigate(`/dashboard/feedback/${buildCardUrl(cardDbId, cardName)}`, { replace: true, state: { refreshTab: "draft", activeTab: "draft" } });
      }}
      onSaveDraft={(_items, _state) => {
        navigate("/dashboard", { replace: true, state: { activeTab: "draft", refreshTab: "draft" } });
      }}
      onMinimize={(liveFeedbackItems, liveWorkspaceState) => {
        const firstScreenshot = liveFeedbackItems.find((i) => i.type === "screenshot" && i.annotatedImage);
        pipStore.set({
          cardDbId,
          workspaceId,
          feedbackName: cardName,
          previewImage: firstScreenshot?.annotatedImage || "",
          feedbackItems: liveFeedbackItems,
          workspaceState: liveWorkspaceState,
        });
        navigate("/dashboard", { replace: true, state: { activeTab: "draft", pipActive: true } });
      }}
      cardId={cardDbId}
      workspaceId={workspaceId}
      initialWorkspaceState={initialWorkspaceState}
      initialFeedbackItems={initialFeedbackItems.length > 0 ? initialFeedbackItems : undefined}
    />
  );
};

export default WorkspacePage;
